import React from "react";
import styled, { keyframes, css } from "styled-components";
import { UploadingFile } from "../../hooks/useS3Upload";
import { FILE_ICONS, getFileIconFromFile } from "../file-icon/fileIcon";
import ToolTip from "../tooltip/ToolTip";

// ===== Animations =====

const progressAnimation = keyframes`
  0% {
    background-position: 0% 50%;
  }
  100% {
    background-position: 100% 50%;
  }
`;

// ===== Styled Components =====

const Container = styled.div<{ $fitToParent: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  ${({ $fitToParent }) =>
    !$fitToParent &&
    css`
      max-height: 200px;
      overflow-y: auto;
    `}
`;

const FileItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 12px;
  background-color: var(--soremo-background);
  border-radius: 6px;
  border: 1px solid #f0f0f0;
`;

const FileHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const FileIcon = styled.span.attrs({
  className: "material-symbols-rounded",
})<{ $status: string }>`
  font-size: 20px;
  color: ${(props) =>
    props.$status === "error"
      ? "var(--soremo-green)"
      : "var(--soremo-light-gray)"};
`;

const FileName = styled.span.attrs({
  className: "bodytext-11",
})`
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--soremo-gray);
`;

const FileSize = styled.span.attrs({
  className: "label8",
})`
  color: var(--soremo-light-gray);
  white-space: nowrap;
`;

const ProgressText = styled.span.attrs({
  className: "label8",
})`
  color: var(--soremo-blue);
  white-space: nowrap;
  min-width: 40px;
  text-align: right;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 2px;
  cursor: pointer;
  color: var(--soremo-light-gray);
  transition: color 0.2s;

  &:hover {
    color: var(--soremo-blue);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const ActionIcon = styled.span.attrs({
  className: "material-symbols-rounded",
})`
  font-size: 18px;
`;

const ProgressBarContainer = styled.div`
  height: 4px;
  background-color: #f0f0f0;
  border-radius: 2px;
  overflow: hidden;
`;

interface ProgressBarFillProps {
  $progress: number;
  $status: string;
}

const ProgressBarFill = styled.div<ProgressBarFillProps>`
  height: 100%;
  width: ${(props) => props.$progress}%;
  border-radius: 2px;
  transition: width 0.3s ease;
  background: ${(props) =>
    props.$status === "error"
      ? "var(--soremo-green)"
      : "linear-gradient(90deg, var(--soremo-blue) 0%, #00c4ff 50%, var(--soremo-blue) 100%)"};
  background-size: ${(props) =>
    props.$status === "uploading" ? "200% 100%" : "100% 100%"};
  ${(props) =>
    props.$status === "uploading" &&
    css`
      animation: ${progressAnimation} 1.5s ease infinite;
    `}
`;

const ErrorMessage = styled.span.attrs({
  className: "label8",
})`
  color: var(--soremo-green);
`;

const spinAnimation = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

/** 後処理中を示すスピナー（進捗行の右側、% 表示と同じ位置に出す） */
const PostProcessSpinner = styled.span.attrs({
  className: "material-symbols-rounded",
})`
  font-size: 18px;
  color: var(--soremo-blue);
  animation: ${spinAnimation} 1.2s linear infinite;
`;

const PostProcessNote = styled.span.attrs({
  className: "label8",
})`
  color: var(--soremo-light-gray);
  text-align: center;
`;

/** 進捗行が 1 つも残っていないときだけ使う、後処理単体の見出し */
const StandaloneLabel = styled.span.attrs({
  className: "bodytext-11",
})`
  flex: 1;
  min-width: 0;
  color: var(--soremo-gray);
`;

const CompletedIcon = styled.span.attrs({
  className: "material-symbols-rounded",
})`
  font-size: 18px;
  color: var(--soremo-blue);
  min-width: 40px;
  text-align: right;
`;

// ===== Helper Functions =====

/** ファイルサイズをフォーマット */
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

/**
 * アップロード行に表示するアイコン名を決定する。
 * エラー時は error アイコン、それ以外はファイル種別に応じたタイプアイコン。
 */
const getFileIconForUploadStatus = (file: File, status: string): string => {
  if (status === "error") return FILE_ICONS.error;
  return getFileIconFromFile(file);
};

// ===== Props =====

export interface UploadProgressBarProps {
  /** アップロード中のファイル一覧 */
  files: UploadingFile[];
  /** キャンセルボタンクリック時のコールバック */
  onCancel?: (fileId: string) => void;
  /** リトライボタンクリック時のコールバック */
  onRetry?: (file: UploadingFile) => void;
  /** コンパクト表示 */
  compact?: boolean;
  /**
   * 自前の高さ制限（max-height: 200px）を外し、親要素の高さに委ねる。
   * Modal 内など、親側がスクロールを担当する場合に true にする。
   */
  fitToParent?: boolean;
  /**
   * S3 転送後に走る後処理（DB 登録・メッセージ送信など）のラベル。
   * `useS3Upload().postProcessLabel` をそのまま渡す。null なら後処理中でない。
   */
  postProcessLabel?: string | null;
}

// ===== Component =====

const UploadProgressBar: React.FC<UploadProgressBarProps> = ({
  files,
  onCancel,
  onRetry,
  compact = false,
  fitToParent = false,
  postProcessLabel = null,
}) => {
  if (files.length === 0 && !postProcessLabel) return null;

  return (
    <Container $fitToParent={fitToParent}>
      {files.map((uploadingFile) => {
        const { status } = uploadingFile;
        // 後処理は S3 転送が終わった行にしか起こらない。ファイル単位の状態を
        // 持たない画面（メッセージ送信など）でも、後処理フェーズ中は完了行を
        // 「処理中」として見せる。
        const postStatus =
          status !== "completed"
            ? undefined
            : (uploadingFile.postStatus ??
              (postProcessLabel ? "running" : undefined));
        // 後処理の失敗もアップロード失敗と同じ見た目にする
        // （S3 には上がっていても、登録できていなければユーザーには失敗）
        const isError = status === "error" || postStatus === "error";
        // 後処理が無い、または後処理まで終わった行だけ ✓ を出す
        const isFullyDone =
          status === "completed" &&
          (postStatus === undefined || postStatus === "done");
        const iconStatus = isError ? "error" : status;
        const errorMessage = isError
          ? (uploadingFile.error ?? uploadingFile.postError)
          : undefined;

        return (
          <FileItemContainer key={uploadingFile.id}>
            <FileHeader>
              <FileInfo>
                <FileIcon $status={iconStatus}>
                  {getFileIconForUploadStatus(uploadingFile.file, iconStatus)}
                </FileIcon>
                <FileName>{uploadingFile.file.name}</FileName>
                {!compact && (
                  <FileSize>{formatFileSize(uploadingFile.file.size)}</FileSize>
                )}
              </FileInfo>

              {(status === "uploading" || status === "pending") && (
                <>
                  <ProgressText>
                    {status === "uploading"
                      ? `${uploadingFile.progress}%`
                      : "待機中..."}
                  </ProgressText>
                  {onCancel && (
                    <ToolTip description="キャンセル">
                      <ActionButton
                        onClick={() => onCancel(uploadingFile.id)}
                        aria-label="キャンセル"
                      >
                        <ActionIcon>close</ActionIcon>
                      </ActionButton>
                    </ToolTip>
                  )}
                </>
              )}

              {/* S3 転送後の後処理も、専用の行を足さずにこの行で状態を示す。
                * 後処理の失敗だけは理由を行の下に出すため、ここには何も出さない。 */}
              {postStatus === "waiting" && <ProgressText>待機中...</ProgressText>}
              {postStatus === "running" && (
                <>
                  <ProgressText>{postProcessLabel}</ProgressText>
                  <PostProcessSpinner>progress_activity</PostProcessSpinner>
                </>
              )}
              {isFullyDone && <CompletedIcon>check_circle</CompletedIcon>}

              {status === "error" && onRetry && (
                <ToolTip description="リトライ">
                  <ActionButton
                    onClick={() => onRetry(uploadingFile)}
                    aria-label="リトライ"
                  >
                    <ActionIcon>refresh</ActionIcon>
                  </ActionButton>
                </ToolTip>
              )}
            </FileHeader>

            {(status === "uploading" || status === "pending") && (
              <ProgressBarContainer>
                <ProgressBarFill
                  $progress={uploadingFile.progress}
                  $status={status}
                />
              </ProgressBarContainer>
            )}

            {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
          </FileItemContainer>
        );
      })}

      {/* 進捗行が残っていない場合だけ、後処理そのものを 1 行で示す */}
      {files.length === 0 && postProcessLabel && (
        <FileItemContainer>
          <FileHeader>
            <FileInfo>
              <StandaloneLabel>{postProcessLabel}</StandaloneLabel>
            </FileInfo>
            <PostProcessSpinner>progress_activity</PostProcessSpinner>
          </FileHeader>
        </FileItemContainer>
      )}

      {postProcessLabel && (
        <PostProcessNote>
          完了するまでこのタブを閉じないでください
        </PostProcessNote>
      )}
    </Container>
  );
};

export default UploadProgressBar;
